// Common badge styling utilities to maintain consistency across the application

export const badgeStyles = {
  // Blue theme for clinic names
  clinic: "text-xs bg-blue-50 text-blue-700 border-blue-200",

  // Secondary theme for escalated status
  escalate: "border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80",

  // Primary theme for consent status
  consent: "border-transparent bg-primary text-primary-foreground hover:bg-primary/80",

  // Destructive theme for declined status
  declined: "border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80",

  // Green theme for enrolled status
  enrolled: "border-transparent bg-green-500 text-white hover:bg-green-600",

  // Pink theme for no status assigned
  noStatusAssigned: "bg-pink-100 text-pink-800",

  // RPM Status themes for Care Specialists
  rpmConsentedInPerson: "bg-blue-100 text-blue-800",
  rpmEnrolledInPerson: "bg-green-100 text-green-800",
  rpmNotApplicableTTCM: "bg-gray-100 text-gray-800",
  rpmNoShow: "bg-pink-100 text-pink-800",
  rpmDeclined: "border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80",

  // Default theme variations
  default: "border-transparent bg-primary text-primary-foreground hover:bg-primary/80",
  secondary: "border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80",

  // Task status themes
  taskDeferred: "border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80",
  taskFollowUp: "border-transparent bg-primary text-primary-foreground hover:bg-primary/80"
} as const;

export type BadgeStyleKey = keyof typeof badgeStyles;

// Helper function to get badge style
export const getBadgeStyle = (styleKey: BadgeStyleKey): string => {
  return badgeStyles[styleKey];
};

// Helper function for CCM status styling
export const getCCMStatusStyle = (status: string): string => {
  switch (status.toLowerCase()) {
    case 'escalate':
      return badgeStyles.escalate;
    case 'consent':
      return badgeStyles.consent;
    case 'declined':
    case 'lvom (cs)':
    case 'nanv (cs)':
      return badgeStyles.declined;
    case 'enrolled':
      return badgeStyles.enrolled;
    case 'no status assigned':
    case '':
      return badgeStyles.noStatusAssigned;
    default:
      return badgeStyles.noStatusAssigned;
  }
};

// Helper function for RPM status styling (Care Specialist specific)
export const getRPMStatusStyle = (status: string): string => {
  const lowerStatus = status.toLowerCase();
  
  // Check if status contains "consent" text - use primary theme like CCM Consent
  if (lowerStatus.includes('consent')) {
    return badgeStyles.consent;
  }
  
  // Check if status contains "enrolled" text - use green theme like CCM Enrolled
  if (lowerStatus.includes('enrolled')) {
    return badgeStyles.enrolled;
  }
  
  // Check if status contains "deferred" text - use destructive theme like CCM Deferred
  if (lowerStatus.includes('deferred')) {
    return badgeStyles.declined;
  }
  
  switch (lowerStatus) {
    case 'not applicable (ttcm)':
      return badgeStyles.rpmNotApplicableTTCM;
    case 'no show':
      return badgeStyles.rpmNoShow;
    case 'declined':
      return badgeStyles.rpmDeclined;
    case 'no status assigned':
    case '':
      return badgeStyles.noStatusAssigned;
    default:
      return badgeStyles.noStatusAssigned;
  }
};

// Helper function for task status styling
export const getTaskStatusStyle = (status: string): string => {
  switch (status.toLowerCase()) {
    case 'deferred':
      return badgeStyles.taskDeferred;
    case 'follow up':
    case 'followup':
      return badgeStyles.taskFollowUp;
    default:
      return badgeStyles.default;
  }
};